import { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { fetchCustomers } from '@/store/slices/contactSlice';
import { ContactList } from '@/components/contacts/ContactList';
import { ContactListControls } from '@/components/contacts/ContactListControls';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { usePageHeader } from '@/contexts/PageHeaderContext';
import { Users } from 'lucide-react';

const AllContacts = () => {
  const { setHeaderData } = usePageHeader();
  const dispatch = useAppDispatch();
  const { contacts, loading, error } = useAppSelector((state) => state.contacts);

  useEffect(() => {
    dispatch(fetchCustomers());
  }, [dispatch]);

  useEffect(() => {
    const totalContacts = contacts.length;
    const activeContacts = contacts.filter((contact: any) => contact.status === 'active').length;
    const leadContacts = contacts.filter((contact: any) => contact.type === 'lead').length;
    setHeaderData({
      title: 'All Contacts',
      description: 'View and manage all contacts in your system',
      icon: Users,
      stats: [
        {
          label: 'Total Contacts',
          value: totalContacts,
          color: 'text-gray-900',
          dotColor: 'bg-blue-500',
        },
        {
          label: 'Active',
          value: activeContacts,
          color: 'text-green-600',
          dotColor: 'bg-green-500',
        },
        {
          label: 'Leads',
          value: leadContacts,
          color: 'text-purple-600',
          dotColor: 'bg-purple-500',
        },
      ],
    });
  }, [contacts, setHeaderData]);
  if (loading) {
    return <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        <Card className="bg-white/80 backdrop-blur-sm border-slate-200/60 shadow-lg shadow-slate-900/5 p-6">
          <div className="space-y-4">
            <Skeleton className="h-8 w-80 bg-slate-200/60" />
            <Skeleton className="h-4 w-60 bg-slate-200/50" />
          </div>
        </Card>
        <Card className="bg-white/80 backdrop-blur-sm border-slate-200/60 shadow-lg shadow-slate-900/5 p-6">
          <div className="space-y-4">
            <Skeleton className="h-10 w-full bg-slate-200/60" />
            <div className="grid gap-4">
              {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-16 w-full bg-slate-200/40" />)}
            </div>
          </div>
        </Card>
      </div>
    </div>;
  }
  if (error) {
    return <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20 p-6">
      <div className="max-w-7xl mx-auto">
        <Card className="bg-white/80 backdrop-blur-sm border-red-200/60 shadow-lg shadow-red-900/5 p-6">
          <div className="text-center space-y-4">
            <div className="w-12 h-12 mx-auto bg-red-100 rounded-full flex items-center justify-center">
              <span className="text-red-600 text-xl">⚠</span>
            </div>
            <div>
              <h3 className="text-lg font-medium text-red-900 mb-2">Error Loading Contacts</h3>
              <p className="text-red-700 mb-4">{error}</p>
              <Button onClick={() => {
                dispatch(fetchCustomers());
              }} className="bg-red-600 hover:bg-red-700 text-white shadow-lg shadow-red-600/25 transition-all duration-200 hover:shadow-red-600/30">
                Try Again
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </div>;
  }
  return <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20">
    <div className="mx-auto px-[30px] py-4 sm:py-6 lg:py-8 max-w-none">
      <div className="space-y-4 sm:space-y-6">
        {/* Header Section */}


        {/* Main Content */}
        <Card className="bg-white/80 backdrop-blur-sm border-slate-200/60 shadow-lg shadow-slate-900/5 transition-all duration-200 hover:shadow-slate-900/10">
          <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6">
            <ContactListControls />
            <div className="border-t border-slate-200/60 pt-6">
              <ContactList contacts={contacts} loading={loading} />
            </div>
          </div>
        </Card>
      </div>
    </div>
  </div>;
};
export default AllContacts;
